import { Enemy } from "../Enemy.js";
import {
    MeshBuilder, StandardMaterial, Color3, Vector3,
    Animation, CubicEase, EasingFunction, DynamicTexture
} from "@babylonjs/core";

/**
 * @class NexusBoss
 * @description Boss final — s'adapte EN TEMPS RÉEL au style du joueur via DataCollector.
 *
 * HP de base : 80
 * Après applyHpMult (IA Edition) : jusqu'à 120 HP si joueur très agressif.
 *
 * PHASES (basées sur % HP) :
 *   Phase 1 > 66% : Observation + tir simple + dash lent
 *   Phase 2 > 33% : Triple tir + mines + dash fréquent
 *   Phase 3 ≤ 33% : Radial + mines denses + dash frénétique + contre-stratégie IA
 *
 * IA EDITION (câblé sur DataCollector) :
 *   - Joueur agressif  → boss s'éloigne, tire plus vite, mines plus denses
 *   - Joueur passif    → boss charge, dash vers le joueur, pression constante
 *   - Joueur qui spam dash → boss anticipe et pose des mines sur la trajectoire
 *   - Joueur prend peu de dégâts → boss passe en mode radial plus tôt
 */
export class NexusBoss extends Enemy {
    constructor(scene, position) {
        super(scene, "NEXUS", position);

        // ─── PV DE BASE CALIBRÉS ───
        // 80 HP de base → applyHpMult() peut monter jusqu'à ~120 si joueur agressif
        this.hp    = 80;
        this.maxHp = 80;

        // ─── STATS DE BASE ───
        this.speed        = 0.07;
        this.currentPhase = 1;

        // Cooldowns — seront modifiés dynamiquement par l'IA Edition
        this.attackCooldown  = 0;
        this.attackInterval  = 85;  // frames entre chaque tir (base)
        this.dashCooldown    = 0;
        this.dashInterval    = 170; // frames entre chaque dash (base)
        this.mineCooldown    = 0;
        this.mineInterval    = 140;

        // Projectiles & mines vivants
        this.projectiles = [];
        this.mines       = [];

        // ─── PROFIL IA (mis à jour par think() à chaque frame) ───
        // Snapshot des données du DataCollector pour éviter de les relire à chaque sous-méthode
        this._aiProfile = {
            aggression:     0.5,  // 0 = passif, 1 = ultra-agressif
            evasion:        0.5,  // 0 = rusheur, 1 = fuyard/dasheur
            damageTaken:    0,    // HP perdus par le joueur — mesure indirecte de l'efficacité du boss
            dashCount:      0,    // Nombre total de dashs
            dashAvgInterval: 0,   // Intervalle moyen (spam = court)
            skillLevel:     0.5,  // 0 = débutant, 1 = expert
        };

        this._phaseJustChanged = false;
        this._counterStrategyActive = false;
        this._counterTimer = 0;

        this._initBase(position);
        this._createHPBar();
    }

    // ══════════════════════════════════════════════════════════════
    // MESH & MATÉRIAUX
    // ══════════════════════════════════════════════════════════════

    _createMesh() {
        // Hitbox invisible
        const hitbox = MeshBuilder.CreatePolyhedron("nexus_hitbox", { type: 1, size: 2.5 }, this.scene);
        hitbox.isVisible = false;

        const visualGroup = MeshBuilder.CreateBox("nexus_visual_group", { size: 0.01 }, this.scene);
        visualGroup.parent = hitbox;
        visualGroup.isVisible = false;
        this.visualGroup = visualGroup;

        // Core octaèdre géant
        const core = MeshBuilder.CreatePolyhedron("nexus_core", { type: 1, size: 2.3 }, this.scene);
        core.parent = visualGroup;

        // Sphère intérieure lumineuse
        const innerCore = MeshBuilder.CreateSphere("nexus_inner", { diameter: 1.6 }, this.scene);
        innerCore.parent = visualGroup;

        // 4 satellites orbitaux
        this._pillars = [];
        for (let i = 0; i < 4; i++) {
            const pillar = MeshBuilder.CreatePolyhedron("nexus_pillar_" + i, { type: 1, size: 0.4 }, this.scene);
            pillar.parent = visualGroup;
            this._pillars.push(pillar);
        }

        this._core      = core;
        this._innerCore = innerCore;
        return hitbox;
    }

    _applyMaterial() {
        const mat = new StandardMaterial("nexusMat", this.scene);
        mat.diffuseColor  = new Color3(0.02, 0.0, 0.02);
        mat.emissiveColor = new Color3(0.7, 0.0, 0.0);
        mat.specularColor = new Color3(0.5, 0.0, 0.0);
        mat.alpha = 0.85;
        this.mesh.material = mat;
        if (this._core) this._core.material = mat;

        const innerMat = new StandardMaterial("nexusInnerMat", this.scene);
        innerMat.diffuseColor  = Color3.Black();
        innerMat.emissiveColor = new Color3(1.0, 0.1, 0.0);
        if (this._innerCore) this._innerCore.material = innerMat;

        const pillarMat = new StandardMaterial("nexusPillarMat", this.scene);
        pillarMat.emissiveColor = new Color3(0.9, 0.0, 0.1);
        this._pillars.forEach(p => p.material = pillarMat);

        // Animation loop : rotation, satellites, pulsation
        this._renderObserver = this.scene.onBeforeRenderObservable.add(() => {
            if (this.isDestroyed || !this.mesh) return;
            const time  = Date.now() * 0.002;
            const speed = this.currentPhase === 1 ? 1.0 : this.currentPhase === 2 ? 1.8 : 2.8;

            if (this._core) {
                this._core.rotation.y += 0.015 * speed;
                this._core.rotation.x  = Math.sin(time) * 0.15;
            }

            const radius = 3.2 + Math.sin(time * 2) * 0.3;
            this._pillars.forEach((pillar, idx) => {
                const angle    = time * 0.8 * speed + (idx * Math.PI / 2);
                pillar.position.x = Math.cos(angle) * radius;
                pillar.position.z = Math.sin(angle) * radius;
                pillar.position.y = Math.sin(time * 3 + idx) * 0.4;
                pillar.rotation.y += 0.03;
                pillar.rotation.z += 0.01;
            });

            if (this._innerCore) {
                const s = 1.0 + Math.sin(time * 6) * 0.12;
                this._innerCore.scaling.setAll(s);
            }
        });

        this._startPulseAnimation();
    }

    _startPulseAnimation() {
        const pulse = new Animation(
            "pulseBoss", "material.emissiveColor", 30,
            Animation.ANIMATIONTYPE_COLOR3, Animation.ANIMATIONLOOPMODE_CYCLE
        );
        pulse.setKeys([
            { frame:  0, value: new Color3(0.7, 0.0, 0.0) },
            { frame: 30, value: new Color3(1.0, 0.0, 0.2) },
            { frame: 60, value: new Color3(0.7, 0.0, 0.0) },
        ]);
        this.mesh.animations.push(pulse);
        this.scene.beginAnimation(this.mesh, 0, 60, true);
    }

    // ══════════════════════════════════════════════════════════════
    // HP BAR
    // ══════════════════════════════════════════════════════════════

    _createHPBar() {
        this.hpBarPlane = MeshBuilder.CreatePlane("nexus_hpbar", { width: 5.0, height: 0.6 }, this.scene);
        this.hpTexture  = new DynamicTexture("nexus_hptex", { width: 640, height: 64 }, this.scene, false);

        const mat = new StandardMaterial("nexus_hpbarmat", this.scene);
        mat.diffuseTexture  = this.hpTexture;
        mat.emissiveTexture = this.hpTexture;
        mat.backFaceCulling = false;

        this.hpBarPlane.material    = mat;
        this.hpBarPlane.parent      = this.mesh;
        this.hpBarPlane.position    = new Vector3(0, 5.0, 0);
        this.hpBarPlane.billboardMode = 7;
        this._updateHPBar();
    }

    _updateHPBar() {
        if (!this.hpTexture) return;
        const ctx   = this.hpTexture.getContext();
        const w = 640, h = 64;

        ctx.clearRect(0, 0, w, h);
        // Fond
        ctx.fillStyle = "rgba(0,0,0,0.65)";
        ctx.fillRect(0, 0, w, h);
        // Barre
        const ratio = Math.max(0, this.hp / this.maxHp);
        ctx.fillStyle = ratio > 0.5 ? "#00ff66" : ratio > 0.25 ? "#ff8800" : "#ff2200";
        ctx.fillRect(4, 4, (w - 8) * ratio, h - 8);
        // Texte
        ctx.fillStyle = "white";
        ctx.font = "bold 28px monospace";
        ctx.textAlign = "center";
        const phaseLabel = ["", "PHASE I", "PHASE II", "PHASE III ☠"][this.currentPhase] ?? "";
        ctx.fillText(`NEXUS — ${this.hp}/${this.maxHp}  ${phaseLabel}`, w / 2, h - 16);

        this.hpTexture.update();
    }

    // ══════════════════════════════════════════════════════════════
    // IA EDITION — applyHpMult override
    // On surcharge pour synchro maxHp (affiché dans la barre)
    // ══════════════════════════════════════════════════════════════

    applyHpMult(mult) {
        if (!mult || mult === 1) return;
        this.hp    = Math.ceil(this.hp    * mult);
        this.maxHp = Math.ceil(this.maxHp * mult);
        this._updateHPBar();
    }

    // ══════════════════════════════════════════════════════════════
    // ACTIVATION / DÉSACTIVATION
    // ══════════════════════════════════════════════════════════════

    activate(position) {
        super.activate(position);
        this.hp           = this.maxHp;
        this.currentPhase = 1;
        this.attackCooldown = 0;
        this.dashCooldown   = 0;
        this.mineCooldown   = 0;
        this._counterStrategyActive = false;
        this._clearProjectiles();
        this._updateHPBar();
    }

    deactivate() {
        if (this.isDestroyed) return;
        super.deactivate();
        this._clearProjectiles();
    }

    _clearProjectiles() {
        (this.projectiles || []).forEach(p => { if (p && !p.isDisposed()) p.dispose(); });
        (this.mines       || []).forEach(m => { if (m && !m.isDisposed()) m.dispose(); });
        this.projectiles = [];
        this.mines       = [];
    }

    // ══════════════════════════════════════════════════════════════
    // THINK — Cerveau principal + lecture IA
    // ══════════════════════════════════════════════════════════════

    think(player, entityManager, aiCollector) {
        if (this.isDestroyed || !player.mesh) return;

        // ── 1. Mettre à jour le profil IA depuis le DataCollector ──
        if (aiCollector) {
            this._aiProfile.aggression      = aiCollector.getAggressionLevel?.() ?? 0.5;
            this._aiProfile.evasion         = aiCollector.getEvasionLevel?.()    ?? 0.5;
            this._aiProfile.damageTaken     = aiCollector.data?.combat?.damageTaken ?? 0;
            this._aiProfile.dashCount       = aiCollector.data?.dash?.count         ?? 0;
            this._aiProfile.dashAvgInterval = aiCollector.data?.dash?.averageInterval ?? 1000;
            // Skill approximatif : peu de dégâts reçus + beaucoup de kills = skilled
            const kills   = aiCollector.data?.combat?.enemiesKilled ?? 0;
            const dmg     = aiCollector.data?.combat?.damageTaken   ?? 0;
            this._aiProfile.skillLevel = Math.min(1, (kills * 0.05) * (1 - Math.min(dmg / 20, 0.8)));
        }

        // ── 2. Déterminer la phase ──
        const hpPercent = this.hp / this.maxHp;
        const oldPhase  = this.currentPhase;
        if      (hpPercent > 0.66) this.currentPhase = 1;
        else if (hpPercent > 0.33) this.currentPhase = 2;
        else                        this.currentPhase = 3;

        if (oldPhase !== this.currentPhase) {
            this._phaseJustChanged = true;
            this._onPhaseChange(oldPhase, this.currentPhase);
            this._updateHPBar();
        } else {
            this._phaseJustChanged = false;
        }

        // ── 3. Appliquer la contre-stratégie IA ──
        this._updateCounterStrategy();

        // ── 4. Rotation d'ambiance ──
        this.mesh.rotation.y += 0.01;
        this.mesh.rotation.x  = Math.sin(Date.now() * 0.001) * 0.2;

        // ── 5. Comportement de phase ──
        const direction = player.mesh.position.subtract(this.mesh.position).normalize();
        const distance  = player.mesh.position.subtract(this.mesh.position).length();

        switch (this.currentPhase) {
            case 1: this._phase1Behavior(player, direction, distance); break;
            case 2: this._phase2Behavior(player, direction, distance); break;
            case 3: this._phase3Behavior(player, direction, distance); break;
        }

        // ── 6. Nettoyer projectiles/mines ──
        this._cleanupProjectiles(player);
    }

    // ══════════════════════════════════════════════════════════════
    // TRANSITIONS DE PHASE
    // ══════════════════════════════════════════════════════════════

    _onPhaseChange(from, to) {
        // Flash visuel de transition de phase
        if (this.mesh?.material) {
            const mat = this.mesh.material;
            const colors = [null, new Color3(1, 0.3, 0), new Color3(1, 0, 0.5), new Color3(1, 1, 0)];
            const flash = colors[to] ?? new Color3(1, 0, 0);
            mat.emissiveColor = flash;
            setTimeout(() => {
                if (mat) mat.emissiveColor = new Color3(0.7, 0.0, 0.0);
            }, 400);
        }

        // Phase 2 : recalibre les intervalles
        if (to === 2) {
            this.attackInterval = 65;
            this.dashInterval   = 130;
            this.mineInterval   = 110;
            // Tir immédiat pour signaler le changement de phase
            this.attackCooldown = 0;
        }

        // Phase 3 : mode frénétique
        if (to === 3) {
            this.attackInterval = 50;
            this.dashInterval   = 90;
            this.mineInterval   = 80;
            this.attackCooldown = 0;
            this.dashCooldown   = 0;
        }
    }

    // ══════════════════════════════════════════════════════════════
    // CONTRE-STRATÉGIE IA — Adapte le comportement au profil joueur
    // ══════════════════════════════════════════════════════════════

    _updateCounterStrategy() {
        const ai = this._aiProfile;

        // ── Joueur très agressif (ki ll beaucoup, peu de dégâts reçus) ──
        // → Boss s'éloigne, tire plus vite, mines plus denses pour forcer à ralentir
        if (ai.aggression > 0.7 && ai.damageTaken < 3) {
            this._applyCounterMods({
                speedMult:        0.8,  // boss recule plus vite
                attackIntervalMult: 0.75,
                mineIntervalMult:   0.70,
                dashIntervalMult:   1.3, // dash moins fréquent (garde ses distances)
                keepDistance:       16,  // distance minimale augmentée
            });
            return;
        }

        // ── Joueur passif / qui évite (beaucoup de dashs, peu de kills) ──
        // → Boss fonce, pression maximale, attaque à courte portée
        if (ai.evasion > 0.7 || (ai.dashCount > 15 && ai.aggression < 0.4)) {
            this._applyCounterMods({
                speedMult:          1.4,
                attackIntervalMult: 1.1,
                mineIntervalMult:   0.80,
                dashIntervalMult:   0.70,
                keepDistance:       6,   // boss colle le joueur
            });
            return;
        }

        // ── Joueur spam-dash (intervalle moyen < 800ms) ──
        // → Boss prédit la trajectoire et pose des mines en avance
        if (ai.dashAvgInterval < 800 && ai.dashCount > 8) {
            this._applyCounterMods({
                speedMult:          1.0,
                attackIntervalMult: 1.0,
                mineIntervalMult:   0.50, // mines très fréquentes
                dashIntervalMult:   0.90,
                predictiveMines:    true,
            });
            return;
        }

        // ── Joueur skilled (peu de dégâts, beaucoup de kills) ──
        // → Boss en mode hybride : plus vite, tirs larges, dashs fréquents
        if (ai.skillLevel > 0.7) {
            this._applyCounterMods({
                speedMult:          1.2,
                attackIntervalMult: 0.85,
                mineIntervalMult:   0.85,
                dashIntervalMult:   0.80,
                keepDistance:       10,
            });
            return;
        }

        // ── Profil neutre : intervalles de base ──
        this._applyCounterMods({
            speedMult:          1.0,
            attackIntervalMult: 1.0,
            mineIntervalMult:   1.0,
            dashIntervalMult:   1.0,
            keepDistance:       10,
        });
    }

    /**
     * Applique les modificateurs de combat calculés par la contre-stratégie.
     * Appelé à chaque frame — utilise les valeurs de base de la phase courante.
     * @private
     */
    _applyCounterMods(mods) {
        // Intervalles de base par phase
        const baseAttack = this.currentPhase === 1 ? 85 : this.currentPhase === 2 ? 65 : 50;
        const baseDash   = this.currentPhase === 1 ? 170 : this.currentPhase === 2 ? 130 : 90;
        const baseMine   = this.currentPhase === 1 ? 9999 : this.currentPhase === 2 ? 110 : 80; // pas de mines en P1

        this._effectiveAttackInterval = Math.round(baseAttack * (mods.attackIntervalMult ?? 1));
        this._effectiveDashInterval   = Math.round(baseDash   * (mods.dashIntervalMult   ?? 1));
        this._effectiveMineInterval   = Math.round(baseMine   * (mods.mineIntervalMult   ?? 1));
        this._effectiveSpeed          = this.speed * (mods.speedMult ?? 1);
        this._keepDistance            = mods.keepDistance ?? 10;
        this._predictiveMines         = mods.predictiveMines ?? false;
    }

    // ══════════════════════════════════════════════════════════════
    // COMPORTEMENTS DE PHASE
    // ══════════════════════════════════════════════════════════════

    _phase1Behavior(player, direction, distance) {
        const keepDist  = this._keepDistance ?? 12;
        const spd       = this._effectiveSpeed ?? this.speed;

        if (distance < keepDist - 2) {
            // Trop proche → recul
            this.mesh.position.addInPlace(direction.scale(-spd));
        } else if (distance > keepDist + 4) {
            // Trop loin → avance lentement
            this.mesh.position.addInPlace(direction.scale(spd * 0.7));
        }

        this.attackCooldown--;
        if (this.attackCooldown <= 0) {
            this._shootSingle(direction);
            this.attackCooldown = this._effectiveAttackInterval ?? this.attackInterval;
        }

        this.dashCooldown--;
        if (this.dashCooldown <= 0 && distance > 10) {
            this._executeBossDash(direction);
            this.dashCooldown = this._effectiveDashInterval ?? this.dashInterval;
        }
    }

    _phase2Behavior(player, direction, distance) {
        const keepDist = this._keepDistance ?? 9;
        const spd      = this._effectiveSpeed ?? this.speed * 1.2;

        if (distance < keepDist - 1) {
            this.mesh.position.addInPlace(direction.scale(-spd));
        } else if (distance > keepDist + 3) {
            this.mesh.position.addInPlace(direction.scale(spd));
        }

        this.attackCooldown--;
        if (this.attackCooldown <= 0) {
            this._shootTriple(direction);
            this.attackCooldown = this._effectiveAttackInterval ?? this.attackInterval;
        }

        this.mineCooldown--;
        if (this.mineCooldown <= 0) {
            if (this._predictiveMines && player.mesh) {
                this._dropPredictiveMine(player);
            } else {
                this._dropMine();
            }
            this.mineCooldown = this._effectiveMineInterval ?? this.mineInterval;
        }

        this.dashCooldown--;
        if (this.dashCooldown <= 0 && distance > 7) {
            this._executeBossDash(direction);
            this.dashCooldown = this._effectiveDashInterval ?? this.dashInterval;
        }
    }

    _phase3Behavior(player, direction, distance) {
        const spd = this._effectiveSpeed ?? this.speed * 1.5;

        // Phase 3 : le boss fonce, ne recule plus
        if (distance > 6) {
            this.mesh.position.addInPlace(direction.scale(spd));
        }

        this.attackCooldown--;
        if (this.attackCooldown <= 0) {
            this._shootRadial();
            this.attackCooldown = this._effectiveAttackInterval ?? this.attackInterval;
        }

        this.mineCooldown--;
        if (this.mineCooldown <= 0) {
            // Phase 3 : double mine à chaque drop
            this._dropMine();
            this._dropMine();
            this.mineCooldown = this._effectiveMineInterval ?? this.mineInterval;
        }

        this.dashCooldown--;
        if (this.dashCooldown <= 0) {
            this._executeBossDash(direction);
            this.dashCooldown = this._effectiveDashInterval ?? this.dashInterval;
        }
    }

    // ══════════════════════════════════════════════════════════════
    // ATTAQUES
    // ══════════════════════════════════════════════════════════════

    _shootSingle(direction) {
        this._createProjectile(this.mesh.position.clone(), direction);
    }

    _shootTriple(direction) {
        [-0.3, 0, 0.3].forEach(angle => {
            this._createProjectile(this.mesh.position.clone(), this._rotateVector(direction, angle));
        });
    }

    _shootRadial() {
        for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            this._createProjectile(
                this.mesh.position.clone(),
                new Vector3(Math.cos(angle), 0, Math.sin(angle))
            );
        }
    }

    _createProjectile(position, direction) {
        const proj = MeshBuilder.CreateSphere("boss_proj", { diameter: 1.0 }, this.scene);
        proj.position = position.clone();
        proj.position.y = 0.8;

        const mat = new StandardMaterial("bossProj_mat", this.scene);
        mat.emissiveColor = new Color3(1, 0.1, 0);
        proj.material = mat;

        proj.metadata = {
            direction: direction.clone(),
            speed:     0.35,
            lifetime:  0,
            maxLifetime: 180,
        };
        this.projectiles.push(proj);
    }

    _dropMine() {
        const offset  = new Vector3((Math.random() - 0.5) * 6, 0, (Math.random() - 0.5) * 6);
        const minePos = this.mesh.position.clone().add(offset);
        minePos.y = 0.05;

        const mine = MeshBuilder.CreateBox("boss_mine", { width: 1.5, height: 0.1, depth: 1.5 }, this.scene);
        mine.position = minePos;

        const mat = new StandardMaterial("bossMine_mat", this.scene);
        mat.emissiveColor = new Color3(1, 0, 1);
        mat.alpha = 0.8;
        mine.material = mat;
        mine.metadata  = { lifetime: 0, maxLifetime: 300 };
        this.mines.push(mine);
    }

    /**
     * Mine prédictive : posée devant le joueur en fonction de sa direction de dash.
     * Contre-stratégie anti-spam-dash.
     * @private
     */
    _dropPredictiveMine(player) {
        if (!player.mesh) return;
        // Estimer la position future : position + lastMoveDirection * dashDistance
        const predictedPos = player.mesh.position.clone();
        if (player.lastMoveDirection) {
            predictedPos.addInPlace(player.lastMoveDirection.scale(5.5));
        }
        predictedPos.y = 0.05;

        const mine = MeshBuilder.CreateBox("boss_mine_pred", { width: 2.0, height: 0.1, depth: 2.0 }, this.scene);
        mine.position = predictedPos;

        const mat = new StandardMaterial("bossMine_pred_mat", this.scene);
        mat.emissiveColor = new Color3(1.0, 0.5, 0); // Orange pour différencier
        mat.alpha = 0.9;
        mine.material = mat;
        mine.metadata  = { lifetime: 0, maxLifetime: 240 };
        this.mines.push(mine);
    }

    _executeBossDash(direction) {
        const dashDistance = 8;
        const targetPos    = this.mesh.position.add(direction.scale(dashDistance));

        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);

        Animation.CreateAndStartAnimation(
            "bossDash", this.mesh, "position",
            60, 18,
            this.mesh.position.clone(), targetPos,
            Animation.ANIMATIONLOOPMODE_CONSTANT,
            ease
        );
    }

    // ══════════════════════════════════════════════════════════════
    // CLEANUP PROJECTILES & MINES
    // ══════════════════════════════════════════════════════════════

    _cleanupProjectiles(player) {
        for (let i = this.projectiles.length - 1; i >= 0; i--) {
            const proj = this.projectiles[i];
            if (!proj || proj.isDisposed()) { this.projectiles.splice(i, 1); continue; }

            proj.position.addInPlace(proj.metadata.direction.scale(proj.metadata.speed));
            proj.metadata.lifetime++;

            if (player?.mesh && proj.intersectsMesh(player.mesh, false)) {
                player.takeDamage();
                proj.dispose();
                this.projectiles.splice(i, 1);
                continue;
            }

            if (proj.metadata.lifetime > proj.metadata.maxLifetime) {
                proj.dispose();
                this.projectiles.splice(i, 1);
            }
        }

        for (let i = this.mines.length - 1; i >= 0; i--) {
            const mine = this.mines[i];
            if (!mine || mine.isDisposed()) { this.mines.splice(i, 1); continue; }

            mine.metadata.lifetime++;
            mine.rotation.y += 0.05;

            if (player?.mesh && mine.intersectsMesh(player.mesh, false)) {
                player.takeDamage();
                mine.dispose();
                this.mines.splice(i, 1);
                continue;
            }

            if (mine.metadata.lifetime > mine.metadata.maxLifetime) {
                mine.dispose();
                this.mines.splice(i, 1);
            }
        }
    }

    // ══════════════════════════════════════════════════════════════
    // DÉGÂTS
    // ══════════════════════════════════════════════════════════════

    takeDamage(amount = 1) {
        const isDead = super.takeDamage(amount);
        this._updateHPBar();
        // Flash rouge sur le hit
        if (!isDead && this.mesh?.material) {
            this.mesh.material.emissiveColor = new Color3(1, 1, 1);
            setTimeout(() => {
                if (this.mesh?.material) this.mesh.material.emissiveColor = new Color3(0.7, 0.0, 0.0);
            }, 80);
        }
        return isDead;
    }

    // ══════════════════════════════════════════════════════════════
    // UTILS
    // ══════════════════════════════════════════════════════════════

    _rotateVector(vec, angle) {
        const cos = Math.cos(angle), sin = Math.sin(angle);
        return new Vector3(vec.x * cos - vec.z * sin, vec.y, vec.x * sin + vec.z * cos);
    }

    dispose() {
        if (this._renderObserver) {
            this.scene.onBeforeRenderObservable.remove(this._renderObserver);
        }
        this._clearProjectiles();
        if (this.hpBarPlane) this.hpBarPlane.dispose();
        if (this.hpTexture)  this.hpTexture.dispose();
        super.dispose();
    }
}